<template>
  <button
    :disabled="props.isDisabled"
    :class="[$style.button, $style[props.type], $style[props.size]]"
    @click="$emit('click')"
  >
    <ButtonSpinner
      v-if="props.isLoading"
      :size="props.size"
      :type="props.type"
    />
    <slot v-else />
  </button>
</template>

<script setup>
import { BUTTON_TYPES, BUTTON_SIZES } from '@/shared/components/UI/Button/button';

import ButtonSpinner from '@/shared/components/UI/Button/ButtonSpinner.vue';

const props = defineProps({
  type: {
    type: String,
    default: BUTTON_TYPES.PRIMARY,
  },
  size: {
    type: String,
    default: BUTTON_SIZES.MEDIUM,
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['click']);
</script>

<style lang="scss" module>
.button {
  @include center-flex;
  width: 100%;
  cursor: pointer;

  &:disabled {
    pointer-events: none;
  }

  &.small {
    @include tiny-text;
    padding: 8px 16px;
    border-radius: 10px;
    min-height: 32px;
  }

  &.medium {
    @include base-text;
    padding: 12px 24px;
    border-radius: 16px;
    min-height: 44px;
  }

  &.primary {
    color: $button-text-invert;
    background-color: $button-primary-default;

    &:hover {
      background-color: $button-primary-hover;
    }

    &:active {
      background-color: $button-primary-active;
    }

    &:disabled {
      color: $button-text-disabled;
      background-color: $button-primary-disabled;
    }
  }

  &.secondary {
    color: $button-text-default;
    background-color: $button-secondary-default;

    &:hover {
      background-color: $button-secondary-hover;
    }

    &:active {
      background-color: $button-secondary-active;
    }

    &:disabled {
      color: $button-text-disabled;
      background-color: $button-secondary-disabled;
    }
  }

  &.outline {
    color: $button-text-default;
    background-color: transparent;
    border: 1px solid $button-outline-default;

    &:hover {
      border-color: $button-outline-hover;
    }

    &:active {
      border-color: $button-outline-active;
    }

    &:disabled {
      color: $button-text-disabled;
      background-color: $button-outline-disabled;
    }
  }

  &.outlineBlue {
    color: $button-text-blue;
    background-color: transparent;
    border: 1px solid $button-outline-blue-default;

    &:hover {
      border-color: $button-outline-blue-default;
    }

    &:active {
      border-color: $button-outline-blue-active;
    }

    &:disabled {
      border-color: $button-outline-blue-disable;
      color: $button-text-disabled;
    }
  }

  &.text {
    background-color: transparent;
    color: $button-text-default;

    &:hover {
      color: $button-text-hover;
    }

    &:active {
      color: $button-text-active;
    }

    &:disabled {
      color: $button-text-disabled;
    }
  }

  &.error {
    background-color: transparent;
    color: $button-text-error;
    border: 1px solid $button-outline-error;

    &:disabled {
      border-color: $button-outline-error-disabled;
      color: $button-text-error-disabled;
    }
  }
}
</style>
